import { useEffect, useState } from 'react';
import { useProgressBarStore } from '../../../../../../../store/hooks/useProgressBarStore';
import { setNestedObjectValues, useFormik } from 'formik';

import { IInputControllerSecondStage, ISecondStageValuesCushingV2, TFile } from './SecondStage.types';
import {
  checkboxInputControllerConfig,
  convertInputControllerSecondStage,
  initialSecondStage,
  validationSecondStageSchema
} from './SecondStage.helper';
import { useInputController } from '../../../../../../shared/ui/form/inputController/useInputController';
import { useDraftCaseApi } from '../../../../../../../apiHooks';
import { useParams } from 'react-router-dom';
import { DiseaseType } from '../../../../../../../interfaces/common/common';
import { Action } from '../../../../../../../store/progressBarStore';

export const useSecondStageVm = () => {
  const { id, type, version } = useParams();
  const [activeFieldset, setActiveFieldset] = useState<string>('');
  const [adenomaFieldsActive, setAdenomaFieldsActive] = useState(false);
  const {
    draftCaseSecondSage,
    draftCaseSecondStageSaved,
    draftCaseSecondStageFiles,
    draftCaseSecondStageFilesWithIndex
  } = useDraftCaseApi();
  const progressBarStore = useProgressBarStore();

  const { inputControllerElements, setActive, setActiveBroadcast } = useInputController<IInputControllerSecondStage>(
    progressBarStore.secondStage
      ? convertInputControllerSecondStage(progressBarStore.secondStage as ISecondStageValuesCushingV2)
      : checkboxInputControllerConfig
  );

  const formik = useFormik<ISecondStageValuesCushingV2>({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    initialValues: (progressBarStore.secondStage as ISecondStageValuesCushingV2) ?? initialSecondStage,
    validationSchema: validationSecondStageSchema,
    onSubmit: async values => {
      await draftCaseSecondSage(values, Number(id), type as DiseaseType, version);
      progressBarStore.setSecondStage(values);
    }
  });

  const { setFieldValue, values } = formik;

  const setFormikValue = (res: TFile, key: string, index?: number) => {
    if (index !== undefined) {
      formik.setFieldValue(key, res);
    }
    formik.setFieldValue(key, res);
  };

  useEffect(() => {
    if (formik.values.currentDiagnosisCushing !== 'zespół ektopowej produkcji ACTH') {
      setFieldValue('currentDiagnosisProductionACTHCheck', '');
    }
    if (formik.values.currentDiagnosisCushing !== 'zespół Cushinga pochodzenia nadnerczowego') {
      setFieldValue('currentDiagnosisAdrenalCushingCheck', '');
    }
  }, [setFieldValue, formik.values.currentDiagnosisCushing]);

  useEffect(() => {
    const isMacroOption = values.imagingResultMrTkMacroMicroadenoma === 'makrogruczolak';

    setAdenomaFieldsActive(isMacroOption);

    if (!isMacroOption) {
      setFieldValue('imagingResultMrTkMacroadenomaInfiltration', '');
      setFieldValue('imagingResultMrTkMacroadenomaInfiltrationRockyBay', '');
      setFieldValue('imagingResultMrTkMacroadenomaInfiltrationRockyScaleKnosp', '');
    }
  }, [setFieldValue, values.imagingResultMrTkMacroMicroadenoma]);

  useEffect(() => {
    const isMacroAdenomaTypeFirst = values.imagingResultMrTkMacroadenomaInfiltration !== 'naciekanie zatok jamistych';

    if (isMacroAdenomaTypeFirst) {
      setFieldValue('imagingResultMrTkMacroadenomaInfiltrationRockyBay', '');
      setFieldValue('infiltrationScaleLeft', '');
      setFieldValue('infiltrationScaleRight', '');
      setFieldValue('infiltrationRightActive', false);
      setFieldValue('infiltrationLeftActive', false);
    }
  }, [setFieldValue, values.imagingResultMrTkMacroadenomaInfiltration]);

  const handleSave = async () => {
    const errors = await formik.validateForm();
    formik.setTouched(setNestedObjectValues(formik.values, true));
    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    }
  };

  const handleResetFieldset = () => {
    setActiveFieldset('');
  };

  const handleDraftSave = async (action: Action) => {
    await draftCaseSecondStageSaved(formik.values, Number(id), type as DiseaseType, version);
    progressBarStore.setSecondStage(formik.values, action);
  };

  const handleChangeSwitchAndCheckbox = (name: keyof ISecondStageValuesCushingV2, value: string | boolean) => {
    formik.setFieldValue(name, value);
  };

  const handleChangeArray = (name: keyof ISecondStageValuesCushingV2, value: string) => {
    let newValues = [...(formik.values[name] as string[])];
    if (newValues.find(element => element === value)) {
      newValues = newValues.filter(element => element !== value);
    } else {
      newValues.push(value);
    }

    formik.setFieldValue(name, newValues);
  };

  const setFileHandler = async (field: string, file: File | null, index?: number) => {
    if (index !== undefined) {
      await draftCaseSecondStageFilesWithIndex(field, Number(id), file, index, setFormikValue);
    } else {
      await draftCaseSecondStageFiles(field, Number(id), file, setFormikValue);
    }
  };

  const addOtherMethod = () => {
    formik.setFieldValue('otherMethodsOtherArray', [
      ...formik.values.otherMethodsOtherArray,
      {
        otherMethod: '',
        otherMethodFiles: null
      }
    ]);
  };

  return {
    activeFieldset,
    adenomaFieldsActive,
    setActiveBroadcast,
    inputControllerElements,
    setActive,
    formik,
    handleSave,
    handleResetFieldset,
    handleChangeSwitchAndCheckbox,
    handleDraftSave,
    handleChangeArray,
    setFileHandler,
    addOtherMethod
  };
};
